import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import cx from 'classnames';
import dayjs from 'dayjs';
import { CDN_TYPE_CRM, imageUrlCDN } from 'utils';

import { CompetitionApi, SiteConfigManager } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { Button, Icon, Label } from '@atlas/ui';

import useTranslate from 'common/I18n/Interpreter';
import SeoPageText from 'components/SeoPage/SeoPageText';
import CompetitionTable from 'components/Tournament/CompetitionTable';
import { useCompetitionsContext } from 'components/Tournament/context/context';

const { PROMOTION_TOURNAMENTS } = COMPONENT_TYPES;
const dateFormat = 'D MMMM YYYY HH:mm';

const Tournament = () => {
  const t = useTranslate();
  const { id } = useParams();
  const competitionPath = SiteConfigManager.getComponentPath(PROMOTION_TOURNAMENTS);

  const [DOMPurify, installDOMPurify] = useState({});
  const [showMore, setShowMore] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);

  const { competitions } = useCompetitionsContext();

  const activeCompetition = useMemo(
    () => competitions.find(item => Number(item.id) === Number(id)),
    [competitions, id],
  );

  useEffect(() => {
    import('dompurify').then(DOMPurifyModule => {
      installDOMPurify({ ...DOMPurifyModule.default });
    });
  }, []);

  useEffect(() => {
    if (!activeCompetition) return;

    CompetitionApi.fetchCompetition(activeCompetition.id).then(({ leaderboard }) => {
      setLeaderboard(leaderboard);
    });
  }, [activeCompetition]);

  const {
    description: { coverImageLink, fullDescription, name, shortDescription } = {},
    timeRange: { endTime, startTime } = {},
  } = activeCompetition || {};

  const clickShowMore = useCallback(() => {
    setShowMore(prev => !prev);
  }, []);

  //TODO: Who is this?
  const imgSrc = imageUrlCDN(coverImageLink, CDN_TYPE_CRM);

  const showMoreClass = cx('tournaments-box__text', { 'tournaments-box__text-less': !showMore });

  const cleanHtml = useMemo(
    () => (DOMPurify.sanitize ? { __html: DOMPurify.sanitize(fullDescription) } : null),
    [DOMPurify, fullDescription],
  );

  return (
    <div className="layout-to-scroll">
      <div className="promotion_page single">
        <Link className="back-to-container ellipsis" to={competitionPath}>
          <Icon className="icon-back" name="back" />
          <h1 className="title-text">{name || t('tournament_not_found')}</h1>
        </Link>
        {activeCompetition && (
          <div className="tournaments__detail">
            <div className="tournaments-box banner-container">
              <img
                alt={name || t('tournament_not_found')}
                className="tournaments-box__banner"
                loading="lazy"
                src={imgSrc}
              />
            </div>

            <div className="tournaments-box">
              <div className="tournaments-box__inner">
                <div className="tournaments-box-date">
                  <div className="tournaments-box-date__text">
                    <Label message="start" />:
                  </div>
                  <div className="tournaments-box-date__amount">
                    {dayjs(startTime).format(dateFormat)}
                  </div>
                </div>

                <div className="tournaments-box-date">
                  <div className="tournaments-box-date__text">
                    <Label message="the_end" />:
                  </div>
                  <div className="tournaments-box-date__amount">
                    {dayjs(endTime).format(dateFormat)}
                  </div>
                </div>
              </div>
            </div>

            <div className="tournaments-box">
              <div className="tournaments-box__text">{shortDescription}</div>
            </div>

            <div className="tournaments-box">
              <div className={showMoreClass} dangerouslySetInnerHTML={cleanHtml} />
              {fullDescription.length > 160 && (
                <Button
                  label={showMore ? 'read_less' : 'read_more'}
                  onClick={clickShowMore}
                  size="medium"
                  type="secondary"
                />
              )}
            </div>

            {leaderboard.length > 0 && (
              <div className="tournaments-box">
                <div className="title-big-bold-text">
                  <span>{t('LeaderBoard')}</span>
                </div>
                <CompetitionTable data={leaderboard} />
              </div>
            )}
          </div>
        )}
      </div>
      <SeoPageText />
    </div>
  );
};

export default Tournament;
